package com.evoapps.evoshift.settings

import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/**
 * SH-196: keeps this device's colours in step with the signed-in person's profile
 * (users/{uid}.appearance):
 * - a pick on the profile (made on another device, or before this one was used) is taken over
 *   whenever the profile brings a new one;
 * - a profile without a pick gets this device's, when it is not the default (picked before
 *   signing in).
 * Picks made here are saved by the Appearance picker (AppearanceViewModel), optimistically.
 */
class ProfileAppearanceSync(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val users: UserRepository,
    private val settings: ThemeSettings
) {
    /** The profile's pick last taken over, per person: a later emission of the same pick changes nothing. */
    private var seen: Pair<String, Appearance?>? = null

    fun start(scope: CoroutineScope): Job = scope.launch {
        getCurrentUser().collect { user ->
            if (user == null) {
                seen = null
                return@collect
            }
            val picked = user.appearance
            if (seen == user.uid to picked) return@collect
            val firstLook = seen?.first != user.uid
            seen = user.uid to picked
            when {
                picked != null -> if (picked != settings.current) settings.choose(picked)
                firstLook && settings.current != Appearance.DEFAULT -> users.updateAppearance(user.uid, settings.current)
            }
        }
    }
}
